import { supportsWebIMConfiguration } from '../../utils/regionFilter';
import ConnectorToolsDialog from './ConnectorToolsDialog';
import { authService } from '../../services/auth';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  authorizeConnector,
  createCustomMcpConnector,
  getCustomMcpConnector,
  updateCustomMcpConnector,
  deleteCustomMcpConnector,
  WEB_MCP_STDIO_UNSUPPORTED_MESSAGE,
  type CustomMcpTransport,
  disconnectConnector,
  getConnectorCatalogSnapshot,
  getConnectorConnectionStatesSnapshot,
  connectorUnavailableReason,
  type ConnectorConnectionState,
  listConnectorCatalog,
  listMyConnectorConnections,
  openExternalUrl,
  parseAuthFields,
  type ConnectorAuthField,
  type ConnectorCatalogItem,
} from '../../services/connector';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { i18nService, type LanguageType } from '../../services/i18n';

type TypeFilter = 'all' | 'mcp' | 'im';

const BUILTIN_WHATSAPP: ConnectorCatalogItem = {
  id: -3,
  slug: 'whatsapp',
  display_name: 'WhatsApp',
  provider: 'Meta',
  category: 'IM',
  auth_type: 'im',
  connector_type: 'im',
};

const LOCAL_LOGOS: Record<string, string> = {
  dingtalk: '/dingding.png',
  feishu: '/feishu.png',
  wechatwork: '/wecom.svg',
  telegram: '/telegram.svg',
  whatsapp: '/whatsapp.svg',
};

const getConnectorType = (item: ConnectorCatalogItem): 'mcp' | 'im' =>
  item.connector_type === 'im' ? 'im' : 'mcp';

const withWebBuiltins = (items: ConnectorCatalogItem[]): ConnectorCatalogItem[] => {
  const slugs = new Set(items.map((item) => item.slug.toLowerCase()));
  return slugs.has('whatsapp') ? items : [...items, BUILTIN_WHATSAPP];
};

const IM_CONNECTOR_PRIORITY = ['whatsapp', 'telegram', 'discord'] as const;

const sortIMConnectors = (items: ConnectorCatalogItem[]): ConnectorCatalogItem[] => {
  const priority = new Map<string, number>(
    IM_CONNECTOR_PRIORITY.map((slug, index) => [slug, index]),
  );
  const sortedIMItems = items
    .filter((item) => item.connector_type === 'im')
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const leftPriority = priority.get(left.item.slug.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
      const rightPriority = priority.get(right.item.slug.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
      return leftPriority - rightPriority || left.index - right.index;
    })
    .map(({ item }) => item);
  let imIndex = 0;
  return items.map((item) => (
    item.connector_type === 'im' ? sortedIMItems[imIndex++] : item
  ));
};

const ConnectorIcon: React.FC<{ item: ConnectorCatalogItem }> = ({ item }) => {
  const slug = item.slug.toLowerCase();
  const logo = item.icon_url || LOCAL_LOGOS[slug];
  if (logo) return <img src={logo} alt="" className="h-full w-full object-contain" />;
  if (slug === 'whatsapp') {
    return (
      <svg viewBox="0 0 48 48" className="h-full w-full" aria-hidden="true">
        <circle cx="24" cy="24" r="19" fill="#25D366" />
        <path d="M14 35l2.2-6A12.5 12.5 0 1 1 20 34.8L14 35Z" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinejoin="round" />
        <path d="M19 17c1 6 5 10 11 12l2.3-3.1-4-2-1.6 1.7c-2.6-1.2-4.5-3-5.7-5.6l1.6-1.6-1.9-3.8L19 17Z" fill="#fff" />
      </svg>
    );
  }
  if (slug === 'qq') {
    return <span className="text-2xl" aria-hidden="true">🐧</span>;
  }
  if (slug === 'wechat') {
    return <span className="flex h-full w-full items-center justify-center rounded-full bg-[#07c160] text-lg font-bold text-white">微</span>;
  }
  const colors: Record<string, string> = {
    'tencent-docs': '#2F80ED', deepwiki: '#273C75', qichacha: '#16A3F8', amap: '#2AB57B', baidupan: '#3478F6',
  };
  return (
    <span
      className="flex h-full w-full items-center justify-center rounded-xl text-lg font-bold text-white"
      style={{ background: colors[slug] || 'linear-gradient(135deg, #365EEC, #4EB5EC)' }}
    >
      {(item.display_name || '?').slice(0, 1)}
    </span>
  );
};

interface ConnectorCatalogSectionProps {
  onConnectIM?: (platform: string) => void;
}

const ConnectorCatalogSection: React.FC<ConnectorCatalogSectionProps> = ({ onConnectIM }) => {
  const [language, setLanguage] = useState<LanguageType>(() => i18nService.getLanguage());
  const [items, setItems] = useState<ConnectorCatalogItem[]>(
    () => getConnectorCatalogSnapshot(i18nService.getLanguage()) || [],
  );
  const [connections, setConnections] = useState<Map<string, ConnectorConnectionState>>(() => getConnectorConnectionStatesSnapshot() || new Map());
  const [appVersion, setAppVersion] = useState('');
  const refreshGeneration = useRef(0);
  const [pendingAuthorization, setPendingAuthorization] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busySlug, setBusySlug] = useState('');
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<TypeFilter>('all');
  const [active, setActive] = useState<ConnectorCatalogItem | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [customOpen, setCustomOpen] = useState(false);
  const [inspectTarget, setInspectTarget] = useState<ConnectorCatalogItem | null>(null);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [custom, setCustom] = useState<{ name: string; url: string; transport: CustomMcpTransport; token: string }>(
    { name: '', url: '', transport: 'streamable-http', token: '' },
  );
  const [customError, setCustomError] = useState('');
  const [customBusy, setCustomBusy] = useState(false);

  const submitCustom = async () => {
    setCustomError('');
    setCustomBusy(true);
    try {
      const result = editingSlug ? await updateCustomMcpConnector(editingSlug, custom) : await createCustomMcpConnector(custom);
      if (!result.success) throw new Error(result.message || i18nService.t('connectorConnectFailed'));
      setCustomOpen(false);
      setCustom({ name: '', url: '', transport: 'streamable-http', token: '' });
      await refresh();
    } catch (createError) {
      setCustomError(createError instanceof Error ? createError.message : String(createError));
    } finally {
      setCustomBusy(false);
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    const generation = ++refreshGeneration.current;
    try {
      // 先刷新目录版本，再读取连接状态，避免旧连接覆盖新版重授权标记。
      const catalog = await listConnectorCatalog(language);
      const states = await listMyConnectorConnections();
      if (generation !== refreshGeneration.current) return;
      setItems(catalog); setConnections(states);
      setActive(current => {
        const next = catalog.find(item => item.slug === current?.slug);
        if (!next || next.config_revision !== current?.config_revision) return null;
        return next;
      });
    } catch (failure) {
      if (generation !== refreshGeneration.current) return;
      setError(failure instanceof Error ? failure.message : String(failure));
    }
    setLoading(false);
  }, [language]);

  useEffect(() => { void refresh(); return () => { refreshGeneration.current++; }; }, [refresh]);
  useEffect(() => { let alive = true; void window.electron.appInfo.getVersion().then(version => { if (alive) setAppVersion(version); }).catch(() => {}); return () => { alive = false; }; }, []);
  useEffect(() => {
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    // 授权在新窗口进行，只刷新状态，不自动重复发起授权请求；五分钟后结束轮询。
    if (!pendingAuthorization) return () => window.removeEventListener('focus', onFocus);
    const interval = window.setInterval(() => { if (!document.hidden) void refresh(); }, 5000);
    const deadline = window.setTimeout(() => setPendingAuthorization(false), 300000);
    return () => { window.removeEventListener('focus', onFocus); clearInterval(interval); clearTimeout(deadline); };
  }, [refresh, pendingAuthorization]);
  useEffect(() => authService.subscribe(() => {
    refreshGeneration.current++; setPendingAuthorization(false); setItems([]); setConnections(new Map()); setActive(null); setCustomOpen(false); setInspectTarget(null);
    if (authService.getAuthState()) void refresh();
  }), [refresh]);

  useEffect(() => i18nService.subscribe(() => {
    setLanguage(i18nService.getLanguage());
  }), []);

  const catalog = useMemo(() => withWebBuiltins(items), [items]);
  const counts = useMemo(() => ({
    all: catalog.length,
    mcp: catalog.filter((item) => getConnectorType(item) === 'mcp').length,
    im: catalog.filter((item) => getConnectorType(item) === 'im').length,
  }), [catalog]);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matchingItems = catalog.filter((item) => {
      if (filter !== 'all' && getConnectorType(item) !== filter) return false;
      if (!query) return true;
      return [item.display_name, item.summary, item.description, item.provider, item.category, item.slug]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
    return sortIMConnectors(matchingItems);
  }, [catalog, filter, search]);

  const connect = async (item: ConnectorCatalogItem, authValues: Record<string, string> = {}) => {
    const reason = connectorUnavailableReason(item, appVersion);
    if (reason) { setError(i18nService.t(reason).replace('{version}', item.client_min_version || '')); return; }
    if (getConnectorType(item) === 'im') {
      if (supportsWebIMConfiguration(item.slug)) onConnectIM?.(item.slug.toLowerCase());
      return;
    }
    const fields = parseAuthFields(item.auth_schema);
    if ((item.auth_type === 'token' || (!item.oauth_ready && item.auth_type === 'oauth')) && fields.length > 0 && !active) {
      setValues({});
      setActive(item);
      return;
    }
    setBusySlug(item.slug);
    setError('');
    try {
      const result = await authorizeConnector(item.slug, authValues);
      if (!result.success) throw new Error(result.message || i18nService.t('connectorConnectFailed'));
      if (result.authorizeUrl) { openExternalUrl(result.authorizeUrl); setPendingAuthorization(true); }
      setActive(null);
      await refresh();
    } catch (connectError) {
      setError(connectError instanceof Error ? connectError.message : String(connectError));
    } finally {
      setBusySlug('');
    }
  };

  const disconnect = async (item: ConnectorCatalogItem) => {
    setBusySlug(item.slug);
    setError('');
    try {
      const result = await disconnectConnector(item.slug);
      if (!result.success) throw new Error(result.message || i18nService.t('connectorDisconnectFailed'));
      await refresh();
    } catch (disconnectError) {
      setError(disconnectError instanceof Error ? disconnectError.message : String(disconnectError));
    } finally {
      setBusySlug('');
    }
  };

  const authFields: ConnectorAuthField[] = active ? parseAuthFields(active.auth_schema) : [];

  return (
    <section>
      <div className="mb-5 flex flex-wrap items-center gap-2.5">
        <div className="relative">
          <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-claude-textSecondary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></svg>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={i18nService.t('connectorSearchPlaceholder')}
            className="w-[min(100%,44rem)] min-w-[18rem] rounded-2xl border border-claude-border bg-white py-2.5 pl-9 pr-3 text-sm text-claude-text outline-none transition focus:border-claude-accent focus:ring-2 focus:ring-claude-accent/10 dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:text-claude-darkText"
          />
        </div>
        {(['all', 'mcp', 'im'] as TypeFilter[]).map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => setFilter(type)}
            className={`rounded-lg px-3 py-2 text-xs font-medium transition-colors ${filter === type ? 'bg-claude-accent/10 text-claude-accent' : 'text-claude-textSecondary hover:bg-black/[0.035] dark:text-claude-darkTextSecondary dark:hover:bg-white/[0.06]'}`}
          >
            {i18nService.t(type === 'all' ? 'connectorFilterAll' : type === 'mcp' ? 'connectorFilterMcp' : 'connectorFilterIm')} {counts[type]}
          </button>
        ))}
        <button type="button" onClick={() => { setEditingSlug(null); setCustom({ name: '', url: '', transport: 'streamable-http', token: '' }); setCustomError(''); setCustomOpen(true); }} className="ml-auto rounded-lg px-2 py-1.5 text-xs text-claude-accent hover:bg-black/[0.04] dark:hover:bg-white/[0.06]">
          添加自定义 MCP
        </button>
        <button
          type="button"
          onClick={() => void refresh()}
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-claude-border text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text dark:border-claude-darkBorder dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
          title={loading ? i18nService.t('loading') : i18nService.t('refresh')}
          aria-label={loading ? i18nService.t('loading') : i18nService.t('refresh')}
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">{error}</div>}
      <div className="grid grid-cols-1 gap-2.5 xl:grid-cols-2">
        {filtered.map((item) => {
          const needsReauth = connections.get(item.slug)?.reauthRequired === true;
          const isConnected = connections.has(item.slug) && !needsReauth;
          const unavailable = connectorUnavailableReason(item, appVersion);
          const isIM = getConnectorType(item) === 'im';
          const unsupportedIM = isIM && (!supportsWebIMConfiguration(item.slug) || !onConnectIM);
          const summary = item.summary || item.description || `${item.provider || ''} ${item.category || ''}`.trim();
          return (
            <article key={item.slug} className="flex min-h-16 items-center gap-3 rounded-2xl border border-claude-border/80 bg-white px-3 py-2 transition-shadow hover:shadow-[0_4px_12px_rgba(15,23,42,0.05)] dark:border-claude-darkBorder/80 dark:bg-claude-darkSurface dark:hover:shadow-none">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-black/[0.025] p-1 dark:bg-white/[0.05]">
                <ConnectorIcon item={item} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="truncate text-[13px] font-semibold text-claude-text dark:text-claude-darkText">{item.display_name}</h3>
                  {item.is_featured && <span className="shrink-0 rounded bg-orange-50 px-1.5 py-0.5 text-[10px] font-medium text-orange-500 dark:bg-orange-900/20">{i18nService.t('featured')}</span>}
                </div>
                <p className="mt-0.5 line-clamp-1 text-[11px] leading-4 text-claude-textSecondary dark:text-claude-darkTextSecondary">{summary}</p>
                {(needsReauth || unavailable) && <p role="status" className="mt-1 text-xs text-amber-600 dark:text-amber-400">{i18nService.t(unavailable || 'connectorReauthRequired').replace('{version}', item.client_min_version || '')}</p>}
                {isIM && <p className="mt-1 text-[11px] leading-4 text-claude-textSecondary dark:text-claude-darkTextSecondary">{i18nService.t(unsupportedIM ? 'connectorIMUnavailable' : 'connectorIMPurpose')}</p>}
              </div>
              {item.owner_user_id === Number(authService.getAuthState()?.user.id) && <div className="flex flex-col text-xs gap-1">
                <button type="button" disabled={busySlug === item.slug} className="text-claude-accent" onClick={async () => {
                  setBusySlug(item.slug);
                  try { const value = await getCustomMcpConnector(item.slug); setCustom(value); setEditingSlug(item.slug); setCustomError(''); setCustomOpen(true); }
                  catch { setError(i18nService.t('connectorEditFailed')); }
                  finally { setBusySlug(''); }
                }}>{i18nService.t('connectorEdit')}</button>
                <button type="button" disabled={busySlug === item.slug} className="text-red-500" onClick={async () => {
                  if (!window.confirm(i18nService.t('connectorDeleteConfirm'))) return;
                  setBusySlug(item.slug);
                  try { const result = await deleteCustomMcpConnector(item.slug); if (!result.success) throw new Error(result.message); await refresh(); }
                  catch { setError(i18nService.t('connectorEditFailed')); }
                  finally { setBusySlug(''); }
                }}>{i18nService.t('connectorDelete')}</button>
              </div>}
              {isConnected && !isIM && !unavailable && <button type="button" onClick={() => setInspectTarget(item)}
                className="text-xs text-claude-accent shrink-0">{i18nService.t('connectorToolsTitle')}</button>}
              {needsReauth && <button type="button" disabled={busySlug === item.slug} onClick={() => void disconnect(item)}
                className="shrink-0 rounded-md px-1.5 py-1 text-xs text-claude-textSecondary hover:text-red-500 disabled:opacity-50 dark:text-claude-darkTextSecondary">
                {i18nService.t('connectorDisconnect')}
              </button>}
              {isConnected ? (
                <div className="flex shrink-0 items-center gap-2 pl-1 text-xs">
                  <span className="inline-flex items-center gap-1.5 text-claude-textSecondary dark:text-claude-darkTextSecondary">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.12)]" />
                    {i18nService.t('connected')}
                  </span>
                  <button
                    type="button"
                    disabled={busySlug === item.slug}
                    onClick={() => void disconnect(item)}
                    className="rounded-md px-1.5 py-1 text-claude-textSecondary transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50 dark:text-claude-darkTextSecondary dark:hover:bg-red-500/10 dark:hover:text-red-400"
                  >
                    {busySlug === item.slug ? i18nService.t('loading') : i18nService.t('connectorDisconnect')}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={busySlug === item.slug || unsupportedIM || Boolean(unavailable)}
                  onClick={() => void connect(item)}
                  className="shrink-0 rounded-xl border border-claude-border px-3 py-1.5 text-xs font-medium text-claude-textSecondary transition-colors hover:border-claude-accent/40 hover:bg-claude-accent/5 hover:text-claude-accent disabled:opacity-50 dark:border-claude-darkBorder dark:text-claude-darkTextSecondary dark:hover:border-claude-accent/50 dark:hover:bg-claude-accent/10"
                >
                  {busySlug === item.slug
                    ? i18nService.t('loading')
                    : isIM
                      ? i18nService.t(unsupportedIM ? 'connectorIMUnsupported' : 'connectorConfigure')
                      : i18nService.t(needsReauth ? 'connectorReauthorize' : 'connectorConnect')}
                </button>
              )}
            </article>
          );
        })}
      </div>
      {!loading && filtered.length === 0 && <div className="py-16 text-center text-sm text-claude-textSecondary dark:text-claude-darkTextSecondary">{i18nService.t('connectorEmpty')}</div>}

      {inspectTarget && <ConnectorToolsDialog slug={inspectTarget.slug} title={inspectTarget.display_name} onClose={() => setInspectTarget(null)} />}
      {customOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={() => setCustomOpen(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl dark:bg-claude-darkSurface" onClick={(event) => event.stopPropagation()}>
            <h2 className="text-base font-semibold text-claude-text dark:text-claude-darkText">{editingSlug ? i18nService.t('connectorEdit') : i18nService.t('connectorAdd')} MCP</h2>
            <p className="mt-1 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
              Web 端通过平台代理调用 http / sse（streamable-http）类型的 MCP 服务；凭证由平台保存，不进入沙箱。暂不支持 stdio 类型。
            </p>
            <div className="mt-4 space-y-3">
              {([['name', '名称', 'text'], ['url', 'MCP 服务地址（http:// 或 https://）', 'text'], ['token', 'Bearer Token（可选）', 'password']] as const).map(([key, label, type]) => (
                <label key={key} className="block">
                  <span className="mb-1 block text-xs font-medium text-claude-text dark:text-claude-darkText">{label}</span>
                  <input
                    type={type}
                    value={custom[key]}
                    onChange={(event) => setCustom((current) => ({ ...current, [key]: event.target.value }))}
                    className="w-full rounded-lg border border-claude-border bg-white px-3 py-2 text-sm outline-none focus:border-claude-accent dark:border-claude-darkBorder dark:bg-claude-darkBg dark:text-claude-darkText"
                  />
                </label>
              ))}
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-claude-text dark:text-claude-darkText">类型</span>
                <select
                  value={custom.transport}
                  onChange={(event) => setCustom((current) => ({ ...current, transport: event.target.value as CustomMcpTransport }))}
                  className="w-full rounded-lg border border-claude-border bg-white px-3 py-2 text-sm outline-none focus:border-claude-accent dark:border-claude-darkBorder dark:bg-claude-darkBg dark:text-claude-darkText"
                >
                  <option value="streamable-http">http（streamable-http）</option>
                  <option value="sse">sse</option>
                  <option value="stdio">stdio（Web 端不支持）</option>
                </select>
              </label>
              {custom.transport === 'stdio' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">{WEB_MCP_STDIO_UNSUPPORTED_MESSAGE}</div>
              )}
              {editingSlug && <p className="text-xs text-amber-600">{i18nService.t('connectorEditTokenHint')}</p>}
              {customError && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">{customError}</div>}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setCustomOpen(false)} className="rounded-lg px-3 py-2 text-sm text-claude-textSecondary hover:bg-black/[0.04] dark:text-claude-darkTextSecondary dark:hover:bg-white/[0.06]">{i18nService.t('cancel')}</button>
              <button
                type="button"
                onClick={() => void submitCustom()}
                disabled={customBusy || custom.transport === 'stdio' || !custom.name.trim() || !custom.url.trim()}
                className="rounded-lg bg-claude-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {customBusy ? i18nService.t('loading') : i18nService.t(editingSlug ? 'save' : 'connectorConnect')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {active && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={() => setActive(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl dark:bg-claude-darkSurface" onClick={(event) => event.stopPropagation()}>
            <h2 className="text-base font-semibold text-claude-text dark:text-claude-darkText">{active.display_name}</h2>
            <p className="mt-1 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">{i18nService.t('connectorCredentialHint')}</p>
            <div className="mt-4 space-y-3">
              {authFields.map((field) => (
                <label key={field.key} className="block">
                  <span className="mb-1 block text-xs font-medium text-claude-text dark:text-claude-darkText">{field.label || field.key}</span>
                  <input
                    type={field.type === 'password' ? 'password' : 'text'}
                    value={values[field.key] || ''}
                    onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.target.value }))}
                    placeholder={field.placeholder}
                    className="w-full rounded-lg border border-claude-border bg-white px-3 py-2 text-sm outline-none focus:border-claude-accent dark:border-claude-darkBorder dark:bg-claude-darkBg dark:text-claude-darkText"
                  />
                  {field.help && <span className="mt-1 block text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">{field.help}</span>}
                </label>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setActive(null)} className="rounded-lg px-3 py-2 text-sm text-claude-textSecondary hover:bg-black/[0.04] dark:text-claude-darkTextSecondary dark:hover:bg-white/[0.06]">{i18nService.t('cancel')}</button>
              <button
                type="button"
                onClick={() => void connect(active, values)}
                disabled={authFields.some((field) => field.required && !values[field.key]?.trim()) || busySlug === active.slug}
                className="rounded-lg bg-claude-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {i18nService.t('connectorConnect')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
};

export default ConnectorCatalogSection;
